import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import * as Turbo from "@hotwired/turbo";
import { handleStaleResponse } from "../lib/handle_stale_response";
import { autosaveDraft, cancel as cancelAutosave } from "../components/lib/autosave_draft";

export default class extends Controller {
  static targets = ["bank", "zone", "chip", "submitButton"];
  static values = {
    group: String,
    submitUrl: String,
    draftUrl: String,
    selectedChipAriaTemplate: String,
  };

  connect() {
    this.selectedChip = null;
    this.submitting = false;
    this.sortables = [];
    this.initSortables();
    this.updateSubmitButton();
  }

  disconnect() {
    this.sortables.forEach((s) => s.destroy());
    this.sortables = [];
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);
  }

  initSortables() {
    if (this.hasBankTarget) {
      this.sortables.push(
        Sortable.create(this.bankTarget, {
          group: this.groupValue,
          animation: 150,
          ghostClass: "fill-blank-chip--ghost",
          chosenClass: "fill-blank-chip--chosen",
          swapThreshold: 0.65,
          draggable: ".fill-blank-chip",
          filter: ".fill-blank-chip--locked, .fill-blank-chip--correct",
          onEnd: () => {
            this.refreshAfterMove();
            this.updateSubmitButton();
            this.scheduleAutosave();
          },
        }),
      );
    }

    this.zoneTargets.forEach((zone) => {
      if (zone.dataset.locked === "true") return;
      this.sortables.push(
        Sortable.create(zone, {
          group: this.groupValue,
          animation: 150,
          ghostClass: "fill-blank-chip--ghost",
          chosenClass: "fill-blank-chip--chosen",
          swapThreshold: 0.65,
          draggable: ".fill-blank-chip",
          filter: ".fill-blank-chip--locked, .fill-blank-chip--correct",
          onAdd: (event) => this.onChipAddedToZone(event, zone),
          onEnd: () => {
            this.refreshAfterMove();
            this.updateSubmitButton();
            this.scheduleAutosave();
          },
        }),
      );
    });
  }

  chipClicked(event) {
    const chip = event.currentTarget;
    if (chip.classList.contains("fill-blank-chip--locked")) return;
    if (chip.classList.contains("fill-blank-chip--correct")) return;
    if (!this.bankTarget.contains(chip)) return;
    event.preventDefault();
    if (this.selectedChip === chip) {
      this.deselectChip();
      return;
    }
    if (this.selectedChip) this.deselectChip();
    this.selectedChip = chip;
    chip.classList.add("fill-blank-chip--selected");
    chip.setAttribute("aria-pressed", "true");
    if (this.hasSelectedChipAriaTemplateValue) {
      if (!chip.dataset.originalLabel) {
        chip.dataset.originalLabel = chip.getAttribute("aria-label") || "";
      }
      chip.setAttribute(
        "aria-label",
        this.selectedChipAriaTemplateValue.replace("%{word}", chip.textContent.trim()),
      );
    }
    // Move keyboard focus to the first interactive empty zone.
    const firstOpen = this.zoneTargets.find(
      (z) => z.dataset.locked !== "true" && !z.querySelector(".fill-blank-chip"),
    );
    if (firstOpen) firstOpen.focus();
  }

  chipKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    this.chipClicked(event);
  }

  zoneKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    this.zoneClicked(event);
  }

  zoneClicked(event) {
    const zone = event.currentTarget;
    if (zone.dataset.locked === "true") return;
    event.preventDefault();

    const occupant = zone.querySelector(".fill-blank-chip");

    if (this.selectedChip && !occupant) {
      const chip = this.selectedChip;
      this.deselectChip();
      zone.appendChild(chip);
    } else if (this.selectedChip && occupant) {
      // swap: occupant → bank, selected → zone
      const chip = this.selectedChip;
      this.deselectChip();
      this.bankTarget.appendChild(occupant);
      zone.appendChild(chip);
    } else if (!this.selectedChip && occupant) {
      this.bankTarget.appendChild(occupant);
    }
    this.refreshAfterMove();
    this.updateSubmitButton();
    this.scheduleAutosave();
  }

  onChipAddedToZone(event, zone) {
    if (zone.dataset.locked === "true") {
      event.from.appendChild(event.item);
      return;
    }
    const chips = zone.querySelectorAll(".fill-blank-chip");
    if (chips.length > 1) {
      chips.forEach((chip) => {
        if (chip !== event.item) this.bankTarget.appendChild(chip);
      });
    }
  }

  deselectChip() {
    if (this.selectedChip) {
      this.selectedChip.classList.remove("fill-blank-chip--selected");
      this.selectedChip.setAttribute("aria-pressed", "false");
      if (this.selectedChip.dataset.originalLabel) {
        this.selectedChip.setAttribute("aria-label", this.selectedChip.dataset.originalLabel);
      }
      this.selectedChip = null;
    }
  }

  refreshAfterMove() {
    this.zoneTargets.forEach((zone) => {
      if (zone.dataset.locked === "true") return;
      const state = zone.dataset.state;
      if (state === "correct" || state === "locked") return;

      zone.classList.remove(
        "fill-blank-drop-zone--wrong",
        "fill-blank-drop-zone--empty",
        "fill-blank-drop-zone--filled",
      );
      const chip = zone.querySelector(".fill-blank-chip");
      if (chip) {
        chip.classList.remove("fill-blank-chip--wrong");
        zone.classList.add("fill-blank-drop-zone--filled");
        zone.dataset.state = "filled";
      } else {
        zone.classList.add("fill-blank-drop-zone--empty");
        zone.dataset.state = "empty";
      }
    });

    if (this.hasBankTarget) {
      this.bankTarget
        .querySelectorAll(".fill-blank-chip")
        .forEach((chip) => {
          chip.classList.remove(
            "fill-blank-chip--wrong",
            "fill-blank-chip--placed",
          );
        });
    }
  }

  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;
    const placements = this.collectPlacements();
    const hasAny = Object.values(placements).some((v) => v && v.length > 0);
    this.submitButtonTarget.disabled = !hasAny || this.submitting;
  }

  collectPlacements() {
    const placements = {};
    this.zoneTargets.forEach((zone) => {
      if (zone.dataset.locked === "true") return;
      const chip = zone.querySelector(".fill-blank-chip");
      placements[zone.dataset.blankId] = chip ? chip.textContent.trim() : "";
    });
    return placements;
  }

  scheduleAutosave() {
    if (this.submitting || !this.hasDraftUrlValue) return;
    autosaveDraft({
      url: this.draftUrlValue,
      body: { submitted: this.collectPlacements() },
    });
  }

  submit(event) {
    event?.preventDefault();
    if (this.submitting) return;
    const placements = this.collectPlacements();
    const hasFilled = Object.values(placements).some((v) => v && v.length > 0);
    if (!hasFilled) return;

    this.submitting = true;
    this.updateSubmitButton();
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const params = new URLSearchParams();
    Object.entries(placements).forEach(([blankId, answer]) => {
      params.append(`submitted[${blankId}]`, answer);
    });

    fetch(this.submitUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
      },
      body: params.toString(),
    })
      .then((response) => {
        if (handleStaleResponse(this, response)) {
          this.submitting = false;
          this.updateSubmitButton();
          return null;
        }
        return response.text();
      })
      .then((html) => {
        if (html) Turbo.renderStreamMessage(html);
      })
      .catch(() => {
        this.submitting = false;
        this.updateSubmitButton();
      });
  }
}
